import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AudioDictationService } from 'src/app/shared/services/audio-dictation.service';
import { Prompt } from 'src/app/shared/models';

@Component({
  selector: 'app-doctor-notes',
  templateUrl: './doctor-notes.component.html',
  styleUrls: ['./doctor-notes.component.scss'],
})
export class DoctorNotesComponent implements OnInit {
  transcription: string = '';
  prompt: string = '';
  doctorNotes: string = '';
  isLoading: boolean = false;
  parsedData: any = null;
  prompts: Prompt[] = [];
  selectedPrompt: Prompt | null = null;
  selectedPromptId: string = '';
  isPublishing: boolean = false;

  @ViewChild('orders') orders: ElementRef;
  @ViewChild('doctorNotes') doctorNotesRef: ElementRef;
  @ViewChild('recommendations') recommendations: ElementRef;

  constructor(
    private audioDictationService: AudioDictationService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.loadPrompts();
  }

  async loadPrompts(): Promise<void> {
    try {
      const promptResp = await this.audioDictationService.getAllPrompts();
      this.prompts = promptResp.data;
    } catch (error) {
      console.error('Failed to load prompts:', error);
      this.toastr.error('Failed to load prompts');
    }
  }

  onPromptDropdownChange(selectedId: string): void {
    if (selectedId) {
      const prompt = this.prompts.find((p) => p.id === +selectedId);
      if (prompt) this.onPromptSelected(prompt);
    } else {
      this.selectedPrompt = null;
      this.selectedPromptId = '';
    }
  }

  onPromptSelected(prompt: Prompt): void {
    this.prompt = prompt.content;
    this.selectedPrompt = prompt;
    this.selectedPromptId = prompt.id.toString();
  }

  async publishPrompt(): Promise<void> {
    if (!this.prompt || !this.prompt.trim()) {
      this.toastr.error('Please enter prompt content before publishing');
      return;
    }

    // Check if prompt contains ${transcription} variable
    const hasTranscriptionVariable = this.prompt.includes('${transcription}');

    let confirmMessage =
      'Are you sure you want to publish a new version of this prompt?';

    if (!hasTranscriptionVariable) {
      confirmMessage =
        "The prompt doesn't have ${transcription} variable support, and the AI might not work properly. Are you sure you want to publish this prompt?";
    }

    if (!confirm(confirmMessage)) {
      return;
    }

    this.isPublishing = true;
    try {
      const response = await this.audioDictationService.publishPrompt(
        this.prompt,
        1
      );
      if (response.code === 200) {
        this.toastr.success('Prompt published successfully');
        await this.loadPrompts(); // Reload prompts to get the updated list
      } else {
        this.toastr.error('Failed to publish prompt');
      }
    } catch (error) {
      console.error('Failed to publish prompt:', error);
      this.toastr.error('Failed to publish prompt');
    } finally {
      this.isPublishing = false;
    }
  }

  generateDoctorNotes(): void {
    if (!this.transcription.trim()) {
      this.toastr.error('Please enter transcription text');
      return;
    }

    this.isLoading = true;
    // this.spinner.show();

    this.doctorNotes = '';

    this.audioDictationService
      .getDoctorNotes(this.transcription, this.prompt)
      .then((response) => {
        if (response.code === 200) {
          try {
            // Try to parse the response as JSON
            this.parsedData = JSON.parse(response.message);
            this.doctorNotes = ''; // Clear raw notes when parsed data is available
          } catch (error) {
            // If parsing fails, treat it as plain text
            this.parsedData = null;
            this.doctorNotes = response.message;
            console.warn(
              'Response is not in JSON format, displaying as raw text'
            );
          }
        } else {
          this.toastr.error(
            'Failed to generate doctor notes. Please check the prompt and try again.'
          );
        }
      })
      .catch((error) => {
        let toastMessage =
          'Failed to generate doctor notes due to an error. Please try again.';
        if (error?.error?.error?.includes('Invalid AI response')) {
          toastMessage =
            'Invalid AI response. Please check the prompt and try again.';
        }

        this.toastr.error(toastMessage);
      })
      .finally(() => {
        this.isLoading = false;
        // this.spinner.hide();
      });
  }

  copySection(section: string): void {
    let textToCopy = '';
    switch (section) {
      case 'orders':
        if (this.orders) {
          textToCopy = this.orders.nativeElement.innerText;
        }
        break;
      case 'doctorNotes':
        if (this.doctorNotesRef) {
          textToCopy = this.doctorNotesRef.nativeElement.innerText;
        }
        break;
      case 'recommendations':
        if (this.recommendations) {
          textToCopy = this.recommendations.nativeElement.innerText;
        }
        break;
    }
    this.copyTextToClipboard(textToCopy);
  }

  copyRawNotes(): void {
    if (this.doctorNotes) {
      this.copyTextToClipboard(this.doctorNotes);
    }
  }

  private copyTextToClipboard(text: string): void {
    if (!text) {
      this.toastr.error('No content to copy');
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.toastr.success('Copied to clipboard');
  }

  getVersionNumber(index: number): number {
    return this.prompts.length - index;
  }
}
